import React from 'react'
import { useNewTranslation } from 'hooks/index'
import { useSelector } from 'react-redux'
import { selectCityTypesByReviewingCampaignCountry } from 'state/constants'
import Text from 'particles/Text'
import { isOverflown } from 'particles/utils'

import { theme } from 'theme'
import { Paper, Popper } from '@mui/material'
import { useStyles } from './TagCell'
import { GridRenderCellParams } from '@mui/x-data-grid-pro'

type LocationData = Record<string, { id: number; name: string }>
interface DataProps {
    value: {
        live: LocationData
        work: LocationData
        hangout: LocationData
    }
}
interface GridCellProps {
    dataProps: DataProps
    width?: number
}

const style = {
    container: {
        lineHeight: '20px',
        letterSpacing: '0.15px',
    },
    text: {
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
        wordBreak: 'break-word',
        overflow: 'hidden',
    } as React.CSSProperties,
}

const LocationCellExpand = React.memo(function GridCellExpand(props: GridCellProps) {
    const [newT] = useNewTranslation()
    const cityTypes = useSelector(selectCityTypesByReviewingCampaignCountry)
    const { width } = props
    const { live, work, hangout } = props.dataProps.value
    const wrapper = React.useRef<HTMLDivElement | null>(null)
    const cellDiv = React.useRef(null)
    const liveRef = React.useRef<HTMLDivElement | null>(null)
    const workRef = React.useRef<HTMLDivElement | null>(null)
    const hangoutRef = React.useRef<HTMLDivElement | null>(null)
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null)
    const classes = useStyles()
    const [showFullCell, setShowFullCell] = React.useState(false)
    const [showPopper, setShowPopper] = React.useState(false)

    const parseLocation = (location: LocationData): string => {
        const result = cityTypes
            .map(type => location[type.id_name] && location[type.id_name].name)
            .filter(item => item)
            .reverse()
            .join(', ')
        return result ? result : '-'
    }

    const handleMouseEnter = () => {
        const isCurrentlyOverflown =
            isOverflown(liveRef.current!) || isOverflown(workRef.current!) || isOverflown(hangoutRef.current!)
        setShowPopper(isCurrentlyOverflown)
        setAnchorEl(cellDiv.current)
        setShowFullCell(true)
    }

    const handleMouseLeave = () => {
        setShowFullCell(false)
    }

    React.useEffect(() => {
        if (!showFullCell) {
            return undefined
        }

        function handleKeyDown(nativeEvent: KeyboardEvent) {
            // IE11, Edge (prior to using Bink?) use 'Esc'
            if (nativeEvent.key === 'Escape' || nativeEvent.key === 'Esc') {
                setShowFullCell(false)
            }
        }

        document.addEventListener('keydown', handleKeyDown)

        return () => {
            document.removeEventListener('keydown', handleKeyDown)
        }
    }, [setShowFullCell, showFullCell])

    return (
        <div ref={wrapper} className={classes.root} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
            <div
                ref={cellDiv}
                style={{
                    height: 1,
                    width,
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                }}
            />
            <div
                className="cellValue"
                style={{
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    wordBreak: 'break-word',
                    overflow: 'hidden',
                }}
            >
                <Text scale="bodyM500" color="primary" style={style.text} ref={liveRef}>
                    {newT('submission.table.currentLocationContent', 'Live: ')}
                    {parseLocation(live)}
                </Text>
                <Text scale="bodyM500" color="primary" style={style.text} ref={workRef}>
                    {newT('submission.table.workLocationContent', 'Work: ')}
                    {parseLocation(work)}
                </Text>
                <Text scale="bodyM500" color="primary" style={style.text} ref={hangoutRef}>
                    {newT('submission.table.hangoutLocationContent', 'Hangout: ')}
                    {parseLocation(hangout)}
                </Text>
            </div>
            {showPopper && (
                <Popper open={showFullCell && anchorEl !== null} anchorEl={anchorEl} style={{ width, marginLeft: -17 }}>
                    <Paper
                        elevation={1}
                        style={{
                            minHeight: wrapper.current!.offsetHeight - 3,
                            background: theme.newColors.neutral50,
                            width,
                            padding: 8,
                            boxShadow:
                                '0px 5px 5px -3px rgba(0, 0, 0, 0.2), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 3px 14px 2px rgba(0, 0, 0, 0.12)',
                        }}
                    >
                        <Text scale="bodyM500" color="primary">
                            {newT('submission.table.currentLocationContent', 'Live: ')}
                            {parseLocation(live)}
                        </Text>
                        <Text scale="bodyM500" color="primary">
                            {newT('submission.table.workLocationContent', 'Work: ')}
                            {parseLocation(work)}
                        </Text>
                        <Text scale="bodyM500" color="primary">
                            {newT('submission.table.hangoutLocationContent', 'Hangout: ')}
                            {parseLocation(hangout)}
                        </Text>
                    </Paper>
                </Popper>
            )}
        </div>
    )
})

export const LocationCell = (params: GridRenderCellParams) => {
    const dataProps: DataProps = params.value as DataProps
    return <LocationCellExpand dataProps={dataProps} width={params.colDef.width} />
}
